/**
 * A PostScript name that carries slider values (Adobe Technical Note 5902, arbitrary instances), split: the family
 * prefix before the first `_`, then one `_<value><tag>` group per axis, in name order (`segoeuivariable_312.000wght`).
 * Mirrors engine/psd-render/core/src/font_instances.rs `split_synthetic_name`; render-rule.json referees both.
 */
export interface SyntheticFontName {
  base: string;
  axes: Array<[tag: string, value: number]>;
}

const AXIS_GROUP = /^(-?\d+(?:\.\d+)?)([A-Za-z][A-Za-z0-9]{3})$/;

// Letters and digits only, as the engine's `key`: a prefix of punctuation alone names no family.
const hasNameCharacters = (s: string): boolean => /[A-Za-z0-9]/.test(s);

/**
 * `name` split as a slider-value name, or `undefined` when any group is not `<-?digits[.digits]><4-char tag>` or holds a
 * value past the `f32` range the engine reads it in. Only ASCII spaces are cut off the ends, as the engine cuts them.
 */
export function splitSyntheticFontName(name: string | undefined): SyntheticFontName | undefined {
  const trimmed = (name ?? '').replace(/^ +| +$/g, '');
  const cut = trimmed.indexOf('_');
  if (cut < 0) return undefined;
  const base = trimmed.slice(0, cut);
  if (!hasNameCharacters(base)) return undefined;
  const axes: SyntheticFontName['axes'] = [];
  for (const group of trimmed.slice(cut + 1).split('_')) {
    const match = AXIS_GROUP.exec(group);
    if (!match) return undefined;
    const value = Number(match[1]) + 0;
    // The engine parses the value as an f32: one that rounds past its range is infinite there, and the name is none.
    if (!Number.isFinite(Math.fround(value))) return undefined;
    axes.push([match[2], value]);
  }

  return { base, axes };
}

// The axis tags OpenType registers; a tag outside them names a foundry axis only in four upper-case letters.
const REGISTERED_AXIS_TAGS = new Set(['wght', 'wdth', 'opsz', 'ital', 'slnt']);
const FOUNDRY_AXIS_TAG = /^[A-Z]{4}$/;

/**
 * True when a group names a registered axis (any case) or a foundry axis (four upper-case ASCII letters, `GRAD`): only
 * such a name is looked up by its family prefix once its full name misses. `Gilroy_800Bold` splits too, but names no
 * axis — it misses as it did before slider-value names were read. Mirrors `SyntheticName::names_a_variation_axis`.
 */
export const namesAVariationAxis = (name: SyntheticFontName): boolean =>
  name.axes.some(([tag]) => REGISTERED_AXIS_TAGS.has(tag.toLowerCase()) || FOUNDRY_AXIS_TAG.test(tag));

/**
 * The family prefix a slider-value name draws from — the name a font file is looked up by once the full name misses;
 * `undefined` for any other name and for one that names no variation axis.
 */
export function syntheticFontBase(name: string | undefined): string | undefined {
  const split = splitSyntheticFontName(name);
  return split && namesAVariationAxis(split) ? split.base : undefined;
}
